import type { CsvCell } from './dashboardExport';

// The columns of a widget's table — the same ones its CSV download has — and how each value
// reads. Pure, so the table and the download can't drift apart and both are tested without a DOM.

export type DashboardTableColumn = {
    key: string;
    label: string;
    numeric: boolean;
    // On screen: full figures with separators, never "1.2M" — a table is where exact numbers
    // are read off.
    format: (value: unknown) => string;
    // In the CSV: the raw number, so a spreadsheet can do its own sums.
    csv: (value: unknown) => CsvCell;
};

const asNumber = (value: unknown): number | null => {
    if (value == null || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
};

export const formatFullNumber = (value: unknown): string => {
    const n = asNumber(value);
    return n == null ? '—' : n.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

// A ratio read as a percentage: 1.0984 → "109.84%".
export const formatRatioPercent = (value: unknown): string => {
    const n = asNumber(value);
    return n == null
        ? '—'
        : `${(n * 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
};

// A value that is already a percentage: 93 → "93%".
export const formatWholePercent = (value: unknown): string => {
    const n = asNumber(value);
    return n == null ? '—' : `${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
};

export const dimensionColumn = (key: string, label: string): DashboardTableColumn => ({
    key,
    label,
    numeric: false,
    format: value => (value == null || value === '' ? '—' : String(value)),
    csv: value => (value == null ? '' : String(value)),
});

export const numberColumn = (
    key: string,
    label: string,
    kind: 'number' | 'ratioPercent' | 'wholePercent' = 'number',
): DashboardTableColumn => ({
    key,
    label,
    numeric: true,
    format:
        kind === 'ratioPercent'
            ? formatRatioPercent
            : kind === 'wholePercent'
            ? formatWholePercent
            : formatFullNumber,
    csv: value => {
        const n = asNumber(value);
        if (n == null) return '';
        return kind === 'ratioPercent' ? Number((n * 100).toFixed(6)) : n;
    },
});

// Numbers in order (blanks last, either way round); text in natural order ("Frame 2" before
// "Frame 10").
export const sortTableRows = <R extends Record<string, unknown>>(
    rows: R[],
    columns: DashboardTableColumn[],
    key: string,
    direction: 'asc' | 'desc',
): R[] => {
    const numeric = columns.find(c => c.key === key)?.numeric ?? false;
    const sign = direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
        if (numeric) {
            const x = asNumber(a[key]);
            const y = asNumber(b[key]);
            if (x == null || y == null) return x == null ? (y == null ? 0 : 1) : -1;
            return sign * (x - y);
        }
        return (
            sign *
            String(a[key] ?? '').localeCompare(String(b[key] ?? ''), undefined, { numeric: true })
        );
    });
};

export const tableToCsvRows = (
    columns: DashboardTableColumn[],
    rows: Record<string, unknown>[],
    totals?: Record<string, unknown> | null,
    totalLabel = 'Total',
): CsvCell[][] => [
    columns.map(c => c.label),
    ...rows.map(row => columns.map(c => c.csv(row[c.key]))),
    ...(totals
        ? [columns.map((c, i) => (c.numeric ? c.csv(totals[c.key]) : i === 0 ? totalLabel : ''))]
        : []),
];
